import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Send, MessageCircle, Loader2, Sparkles, Trash2, Ban } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { sanitizeMessage } from "@/lib/sanitizeText";

interface Conversation {
  id: string;
  visitor_name: string | null;
  user_id: string | null;
  last_message_at: string;
  created_at: string;
}

interface Msg {
  id: string;
  role: "visitor" | "bot" | "admin";
  content: string;
  created_at: string;
}

interface AutoReply {
  id: string;
  reply_text: string;
  trigger_keywords: string[];
}

const AdminChatPanel = ({ sessionToken }: { sessionToken: string }) => {
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [autoReplies, setAutoReplies] = useState<AutoReply[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const authFailedRef = useRef(false);
  const { toast } = useToast();

  const isAdminTokenFresh = (token: string | null | undefined): boolean => {
    const expiry = Number.parseInt(String(token || "").split(":")[0], 10);
    return Number.isFinite(expiry) && Date.now() < expiry;
  };

  // If our admin token expired, the edge function returns 401 forever — stop polling and force a re-login.
  const handleAuthFailure = async (error: any, data: any): Promise<boolean> => {
    let responseError = "";
    try {
      const context = error?.context;
      const payload = typeof context?.clone === "function" ? await context.clone().json() : null;
      responseError = JSON.stringify(payload || "");
    } catch {}

    const status = error?.context?.status ?? error?.status;
    const msg = `${data?.error || ""} ${error?.message || ""} ${responseError}`.toLowerCase();
    if (!isAdminTokenFresh(sessionToken) || status === 401 || msg.includes("unauthorized") || msg.includes("401")) {
      if (authFailedRef.current) return true;
      authFailedRef.current = true;
      try { sessionStorage.removeItem("admin_session_token"); } catch {}
      toast({ title: "Session expired", description: "Please sign in again.", variant: "destructive" });
      setTimeout(() => window.location.reload(), 600);
      return true;
    }
    return false;
  };

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const loadConvs = async () => {
      if (stopped) return;
      try {
        if (!isAdminTokenFresh(sessionToken)) {
          if (await handleAuthFailure(null, { error: "unauthorized" })) stopped = true;
          return;
        }
        const { data, error } = await supabase.functions.invoke("admin-chat-reply", {
          body: { action: "list_conversations", sessionToken },
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (error || data?.error) {
          if (await handleAuthFailure(error, data)) { stopped = true; if (timer) clearInterval(timer); }
          return;
        }
        if (data?.conversations) setConvs(data.conversations);
      } catch (error) {
        if (await handleAuthFailure(error, null)) { stopped = true; if (timer) clearInterval(timer); }
      }
    };
    loadConvs();
    (supabase as any)
      .from("chat_auto_replies")
      .select("id, reply_text, trigger_keywords")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }: any) => setAutoReplies(data ?? []));

    timer = setInterval(loadConvs, 4000);
    return () => { if (timer) clearInterval(timer); };
  }, [sessionToken]);

  useEffect(() => {
    if (!activeId) return;
    let active = true;
    let timer: ReturnType<typeof setInterval> | undefined;
    const loadMsgs = async () => {
      if (!active) return;
      try {
        if (!isAdminTokenFresh(sessionToken)) {
          if (await handleAuthFailure(null, { error: "unauthorized" })) active = false;
          return;
        }
        const { data, error } = await supabase.functions.invoke("admin-chat-reply", {
          body: { action: "list_messages", conversation_id: activeId, sessionToken },
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!active) return;
        if (error || data?.error) {
          if (await handleAuthFailure(error, data)) { active = false; if (timer) clearInterval(timer); }
          return;
        }
        if (data?.messages) setMessages(data.messages);
      } catch (error) {
        if (await handleAuthFailure(error, null)) { active = false; if (timer) clearInterval(timer); }
      }
    };
    loadMsgs();
    timer = setInterval(loadMsgs, 3000);
    return () => { active = false; if (timer) clearInterval(timer); };
  }, [activeId, sessionToken]);

  useEffect(() => {
    if (!activeId && convs.length > 0) setActiveId(convs[0].id);
  }, [convs, activeId]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // Only auto-scroll if user is already near the bottom; never yank the page.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 80) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Find best matching auto-reply for the latest visitor message
  const lastVisitorMsg = [...messages].reverse().find((m) => m.role === "visitor");
  const suggestion: AutoReply | null = (() => {
    if (!lastVisitorMsg) return null;
    const lower = lastVisitorMsg.content.toLowerCase();
    return autoReplies.find((r) =>
      (r.trigger_keywords || []).some((kw) => lower.includes(kw.toLowerCase()))
    ) ?? null;
  })();

  const sendReply = async (textOverride?: string) => {
    const text = (textOverride ?? reply).trim();
    if (!text || !activeId) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-chat-reply", {
        body: { conversation_id: activeId, content: text, sessionToken },
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (error || (data as any)?.error) {
        if (await handleAuthFailure(error, data)) return;
        throw error || new Error((data as any).error);
      }
      setReply("");
      toast({ title: "Reply sent", description: "Visitor will see it instantly." });
    } catch (e: any) {
      toast({
        title: "Failed to send reply",
        description: e?.message || "Check your admin session and try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const activeConv = convs.find((c) => c.id === activeId);

  const performAction = async (action: "delete_conversation" | "block_visitor", extra: Record<string, unknown> = {}) => {
    if (!activeId) return;
    try {
      const { data, error } = await supabase.functions.invoke("admin-chat-reply", {
        body: { action, conversation_id: activeId, sessionToken, ...extra },
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (error || (data as any)?.error) {
        if (await handleAuthFailure(error, data)) return;
        throw error || new Error((data as any).error);
      }
      toast({
        title: action === "delete_conversation" ? "Conversation deleted" : "Visitor blocked",
        description: action === "block_visitor" ? "They cannot start new chats." : undefined,
      });
      setConvs((prev) => prev.filter((c) => c.id !== activeId));
      setActiveId(null);
      setMessages([]);
    } catch (e: any) {
      toast({ title: "Action failed", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="px-3 py-2 shrink-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageCircle className="w-3.5 h-3.5 text-primary" /> Live Visitor Chats
          {convs.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1.5">{convs.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 px-2 pb-2">
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 h-full min-h-[320px]">
          <div className="border rounded-lg overflow-y-auto bg-muted/20 max-h-[220px] self-start w-full">
            {convs.length === 0 ? (
              <div className="p-4 text-center text-xs text-muted-foreground">
                <MessageCircle className="w-6 h-6 mx-auto mb-1.5 opacity-40" />
                No conversations yet.
              </div>
            ) : (
              convs.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-left px-2 py-1.5 border-b hover:bg-muted/60 transition-colors ${
                    activeId === c.id ? "bg-background border-l-2 border-l-primary" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="text-xs font-medium truncate">{c.visitor_name || "Visitor"}</p>
                    {c.user_id && <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 shrink-0">User</Badge>}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(c.last_message_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </button>
              ))
            )}
          </div>

          <div className="border rounded-lg flex flex-col min-w-0 min-h-0 bg-background max-h-[280px] md:max-h-[300px]">
            {!activeId ? (
              <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                Select a conversation
              </div>
            ) : (
              <>
                <div className="border-b px-2.5 py-1.5 bg-muted/30 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{activeConv?.visitor_name || "Visitor"}</p>
                    <p className="text-[10px] text-muted-foreground">Live chat • auto-refresh 3s</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
                          <AlertDialogDescription>
                            All messages from <strong>{activeConv?.visitor_name || "this visitor"}</strong> will be permanently removed. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => performAction("delete_conversation")}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive hover:text-destructive">
                          <Ban className="w-3.5 h-3.5 mr-1" /> Block
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Block this visitor?</AlertDialogTitle>
                          <AlertDialogDescription>
                            <strong>{activeConv?.visitor_name || "This visitor"}</strong> will be prevented from starting new chats or sending messages. The conversation will also be deleted.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => performAction("block_visitor", { delete: true })}
                          >
                            Block & Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-muted/10">
                  {messages.map((m) => {
                    const safe = sanitizeMessage(m.content);
                    const isExpanded = expandedIds.has(m.id);
                    const displayText = isExpanded ? String(m.content ?? "") : safe.text;
                    return (
                      <div key={m.id} className={`flex ${m.role === "visitor" ? "justify-start" : "justify-end"}`}>
                        <div
                          className={`max-w-[80%] px-2.5 py-1.5 rounded-2xl text-xs overflow-hidden ${
                            m.role === "visitor"
                              ? "bg-muted"
                              : m.role === "admin"
                              ? "bg-primary text-primary-foreground"
                              : "bg-accent/20 border border-accent/30"
                          }`}
                        >
                          <p className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{m.role}</p>
                          <p
                            className={`whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] ${
                              isExpanded ? "max-h-64 overflow-y-auto" : ""
                            }`}
                            dir="auto"
                          >
                            {displayText}
                          </p>
                          {safe.truncated && (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(m.id)}
                              className="text-[10px] mt-1 underline opacity-70 hover:opacity-100"
                            >
                              {isExpanded ? "View less" : "View full message"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                </div>

                {suggestion && (
                  <AiRefinePanel
                    sessionToken={sessionToken}
                    visitorMessages={messages}
                    seedSuggestion={suggestion.reply_text}
                    sending={sending}
                    onSend={(text) => sendReply(text)}
                    onPushToInput={(text) => setReply(text)}
                  />
                )}

                <div className="border-t p-1.5 space-y-1.5">
                  {autoReplies.length > 0 && (
                    <div className="flex items-center gap-1 overflow-x-auto pb-0.5 -mx-0.5 px-0.5">
                      <Sparkles className="w-3 h-3 text-primary shrink-0" />
                      {autoReplies.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => sendReply(r.reply_text)}
                          disabled={sending}
                          title={r.reply_text}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors disabled:opacity-50 whitespace-nowrap shrink-0"
                        >
                          {r.reply_text.slice(0, 28)}{r.reply_text.length > 28 ? "…" : ""}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <Input
                      placeholder="Type a reply…"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      disabled={sending}
                      className="h-8 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <Button size="sm" className="h-8 px-2.5" onClick={() => sendReply()} disabled={!reply.trim() || sending}>
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- AI multi-turn refinement panel ----------
// Opens when an auto-reply suggestion is matched. Admin can chat back-and-forth
// with the AI to refine the reply, then send the final draft to the visitor or
// push it into the input box to edit manually before sending.
interface AiTurn { role: "user" | "assistant"; content: string }
const AiRefinePanel = ({
  sessionToken,
  visitorMessages,
  seedSuggestion,
  sending,
  onSend,
  onPushToInput,
}: {
  sessionToken: string;
  visitorMessages: Msg[];
  seedSuggestion: string;
  sending: boolean;
  onSend: (text: string) => void;
  onPushToInput: (text: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState<AiTurn[]>([]);
  const [adminMsg, setAdminMsg] = useState("");
  const [thinking, setThinking] = useState(false);
  const { toast } = useToast();

  const latest = conversation.length > 0
    ? conversation[conversation.length - 1].content
    : seedSuggestion;

  const refine = async () => {
    const turn = adminMsg.trim();
    if (!turn) return;
    setThinking(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-ai-suggest", {
        body: {
          sessionToken,
          mode: "visitor_chat",
          context: { visitor_messages: visitorMessages.map((m) => ({ role: m.role, content: m.content })) },
          aiConversation: conversation.length > 0
            ? conversation
            : [{ role: "assistant", content: seedSuggestion }],
          adminTurn: turn,
        },
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      setConversation((data as any).conversation as AiTurn[]);
      setAdminMsg("");
    } catch (e: any) {
      toast({ title: "AI refine failed", description: e.message, variant: "destructive" });
    } finally {
      setThinking(false);
    }
  };

  if (!open) {
    return (
      <div className="border-t bg-accent/10 px-3 py-2 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
        <p className="text-xs text-muted-foreground truncate flex-1" title={seedSuggestion}>
          <span className="font-medium text-foreground">Suggested:</span> {seedSuggestion}
        </p>
        <Button size="sm" variant="default" className="h-7 px-2 text-xs shrink-0" onClick={() => onSend(seedSuggestion)} disabled={sending}>
          Send
        </Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => setOpen(true)}>
          <Sparkles className="w-3 h-3 mr-1" /> Refine with AI
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t bg-accent/10 px-3 py-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-primary" /> AI refinement
        </p>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setOpen(false)} title="Stop AI conversation">
          Interrupt
        </Button>
      </div>

      <div className="max-h-44 overflow-y-auto space-y-1.5 rounded-md border bg-background p-2">
        <div className="text-[11px]">
          <span className="font-medium text-primary">AI:</span>{" "}
          <span className="whitespace-pre-wrap break-words" dir="auto">{seedSuggestion}</span>
        </div>
        {conversation.map((t, i) => (
          <div key={i} className="text-[11px]">
            <span className={`font-medium ${t.role === "user" ? "text-foreground" : "text-primary"}`}>
              {t.role === "user" ? "You:" : "AI:"}
            </span>{" "}
            <span className="whitespace-pre-wrap break-words" dir="auto">{t.content}</span>
          </div>
        ))}
        {thinking && (
          <div className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> AI is thinking…
          </div>
        )}
      </div>

      <div className="flex gap-1.5">
        <Input
          placeholder="Ask AI to refine: shorter, friendlier, add link…"
          value={adminMsg}
          onChange={(e) => setAdminMsg(e.target.value)}
          disabled={thinking}
          className="h-8 text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              refine();
            }
          }}
        />
        <Button size="sm" className="h-8 px-2.5" onClick={refine} disabled={!adminMsg.trim() || thinking}>
          {thinking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>

      <div className="flex items-center justify-end gap-1.5">
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onPushToInput(latest)} disabled={sending}>
          Edit in input
        </Button>
        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => onSend(latest)} disabled={sending}>
          Send latest to visitor
        </Button>
      </div>
    </div>
  );
};

export default AdminChatPanel;
